// src/pages/Discovery.jsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabaseClient"; // match your project path

const PAGE_SIZE = 10;
const FUNCTIONS_URL = process.env.REACT_APP_SUPABASE_FUNCTIONS_URL;
const ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Simple CSV parser (no library): supports quoted fields and commas inside quotes
function parseCSV(text) {
  const rows = [];
  let cur = "";
  let inQuotes = false;
  let row = [];
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    const next = text[i + 1];
    if (c === '"' && inQuotes && next === '"') {
      cur += '"';
      i++;
    } else if (c === '"') {
      inQuotes = !inQuotes;
    } else if (c === "," && !inQuotes) {
      row.push(cur);
      cur = "";
    } else if ((c === "\n" || c === "\r") && !inQuotes) {
      if (c === "\r" && next === "\n") i++; // handle CRLF
      row.push(cur);
      rows.push(row);
      row = [];
      cur = "";
    } else {
      cur += c;
    }
  }
  if (cur.length || row.length) {
    row.push(cur);
    rows.push(row);
  }
  // trim BOM and whitespace
  return rows.map((r) => r.map((v) => v.replace(/^\uFEFF/, "").trim()));
}

export default function Discovery() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [busyIds, setBusyIds] = useState(new Set());     // per-row in-flight
  const [hiddenIds, setHiddenIds] = useState(new Set()); // force-hide rows now
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sourceFilter, setSourceFilter] = useState("all"); // 'all' | 'twitter' | 'rss'
  const [scoreSort, setScoreSort] = useState("desc");      // 'desc' | 'asc'
  const [bulkBusy, setBulkBusy] = useState(false);         // disables bulk buttons while running

  // ---- Quick Add form state ----
  const [qaOpen, setQaOpen] = useState(false);
  const [qaName, setQaName] = useState("");
  const [qaUrl, setQaUrl] = useState("");
  const [qaSource, setQaSource] = useState("twitter"); // 'twitter' | 'rss'
  const [qaSnippet, setQaSnippet] = useState("");
  const [qaScore, setQaScore] = useState(5);
  const [qaBusy, setQaBusy] = useState(false);

  // ---- Add URL (scrape-to-ingest) state ----
  const [addUrlOpen, setAddUrlOpen] = useState(false);
  const [addUrlValue, setAddUrlValue] = useState("");
  const [addUrlBusy, setAddUrlBusy] = useState(false);

  // ---- CSV Import state ----
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [csvPreview, setCsvPreview] = useState([]);
  const [csvError, setCsvError] = useState("");
  const [csvBusy, setCsvBusy] = useState(false);
  const csvTemplate = `name,profile_url,source,context_snippet,lead_score
"Test User","https://example.com/user","rss","Collector in NYC galleries",7
"Jane Doe","https://twitter.com/janedoe","twitter","Tweets about Bitcoin art",8`;

  // Load
  async function fetchRows() {
    setLoading(true);
    const { data, error } = await supabase
      .from("leads_discovery")
      .select("*")
      .order("date_added", { ascending: false });
    if (error) {
      console.error(error);
      setBanner({ type: "error", msg: "Failed to load discovery leads." });
    } else {
      setRows(data || []);
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchRows();
  }, []);

  // Helpers
  const normalize = (s) => (s || "").toString().toLowerCase();
  const bumpBadge = (delta) => {
    window.dispatchEvent(
      new CustomEvent("discovery-count-dirty", { detail: { delta } })
    );
  };

  // ---- Add URL: scrape a page's title/description straight into Discovery ----
  const saveAddUrl = async () => {
    if (!addUrlValue.trim()) {
      setBanner({ type: "error", msg: "Paste a URL first." });
      return;
    }
    setAddUrlBusy(true);
    try {
      const res = await fetch(`${FUNCTIONS_URL}/scrape-url`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: ANON_KEY,
          Authorization: `Bearer ${ANON_KEY}`,
        },
        body: JSON.stringify({ url: addUrlValue.trim() }),
      });
      if (!res.ok) throw new Error(await res.text());
      await res.json();
      setBanner({ type: "success", msg: "URL scraped and added to Discovery." });
      setAddUrlValue("");
      bumpBadge(1);
      await fetchRows();
    } catch (e) {
      setBanner({ type: "error", msg: `Add URL failed: ${e.message}` });
    } finally {
      setAddUrlBusy(false);
    }
  };
  const setBusy = (id, on) =>
    setBusyIds((prev) => {
      const next = new Set(prev);
      on ? next.add(id) : next.delete(id);
      return next;
    });
  const hideNow = (ids) =>
    setHiddenIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  const restoreHidden = (ids) =>
    setHiddenIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });

  // Filters + sorting + pagination
  const filtered = useMemo(() => {
    const q = normalize(search);
    let list = rows.filter((r) => !hiddenIds.has(r.id));

    if (sourceFilter !== "all") {
      list = list.filter((r) => normalize(r.source) === sourceFilter);
    }
    if (q) {
      list = list.filter((r) =>
        [r.name, r.source, r.context_snippet, r.profile_url]
          .map(normalize)
          .some((v) => v.includes(q))
      );
    }
    list.sort((a, b) =>
      scoreSort === "desc"
        ? (b.lead_score ?? 0) - (a.lead_score ?? 0)
        : (a.lead_score ?? 0) - (b.lead_score ?? 0)
    );
    return list;
  }, [rows, search, hiddenIds, sourceFilter, scoreSort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const start = (pageSafe - 1) * PAGE_SIZE;
  const pageRows = filtered.slice(start, start + PAGE_SIZE);

  // Selection
  const toggleSelect = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const isAllOnPageSelected = pageRows.every((r) => selectedIds.has(r.id));
  const toggleSelectAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (isAllOnPageSelected) {
        pageRows.forEach((r) => next.delete(r.id));
      } else {
        pageRows.forEach((r) => next.add(r.id));
      }
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  // RPC wrappers
  const rpcApproveBulk = (ids) =>
    supabase.rpc("approve_discovery_leads_bulk", { p_ids: ids });
  const rpcRejectBulk = (ids) =>
    supabase.rpc("reject_discovery_leads_bulk", { p_ids: ids });
  const rpcRescore = (ids) =>
    supabase.rpc("rescore_discovery_leads", { p_ids: ids });

  // Instant badge + hide for bulk
  const instantHideAndBadge = (ids) => {
    hideNow(ids);
    bumpBadge(-ids.length);
  };

  // Single Approve/Reject (use bulk path)
  async function approve(id) {
    if (busyIds.has(id)) return;
    setBusy(id, true);
    instantHideAndBadge([id]);

    const { error } = await rpcApproveBulk([id]);
    if (error) {
      console.error(error);
      setBanner({ type: "error", msg: "Approve failed. Restoring item…" });
      restoreHidden([id]);
      bumpBadge(0);
    } else {
      setBanner({ type: "success", msg: "Lead approved → moved to Leads." });
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
    setBusy(id, false);
  }

  async function reject(id) {
    if (busyIds.has(id)) return;
    setBusy(id, true);
    instantHideAndBadge([id]);

    const { error } = await rpcRejectBulk([id]);
    if (error) {
      console.error(error);
      setBanner({ type: "error", msg: "Reject failed. Restoring item…" });
      restoreHidden([id]);
      bumpBadge(0);
    } else {
      setBanner({ type: "success", msg: "Lead rejected (archived)." });
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
    setBusy(id, false);
  }

  // Bulk actions
  async function approveSelected() {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    instantHideAndBadge(ids);
    clearSelection();

    const { error } = await rpcApproveBulk(ids);
    if (error) {
      console.error(error);
      setBanner({ type: "error", msg: "Bulk approve failed. Restoring items…" });
      restoreHidden(ids);
      bumpBadge(0);
    } else {
      setBanner({ type: "success", msg: `Approved ${ids.length} lead(s).` });
    }
  }

  async function rejectSelected() {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    instantHideAndBadge(ids);
    clearSelection();

    const { error } = await rpcRejectBulk(ids);
    if (error) {
      console.error(error);
      setBanner({ type: "error", msg: "Bulk reject failed. Restoring items…" });
      restoreHidden(ids);
      bumpBadge(0);
    } else {
      setBanner({ type: "success", msg: `Rejected ${ids.length} lead(s).` });
    }
  }

  // Re-score actions
  async function rescoreSelected() {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkBusy(true);
    const { error } = await rpcRescore(ids);
    if (error) {
      console.error(error);
      setBanner({ type: "error", msg: "Re-score failed." });
    } else {
      setBanner({ type: "success", msg: `Re-scored ${ids.length} lead(s).` });
      await fetchRows(); // refresh to show new scores
    }
    setBulkBusy(false);
  }

  async function rescoreAllFiltered() {
    const ids = filtered.map((r) => r.id);
    if (ids.length === 0) return;
    setBulkBusy(true);
    const { error } = await rpcRescore(ids);
    if (error) {
      console.error(error);
      setBanner({ type: "error", msg: "Re-score (all) failed." });
    } else {
      setBanner({ type: "success", msg: `Re-scored ${ids.length} lead(s).` });
      await fetchRows();
    }
    setBulkBusy(false);
  }

  // ---- Quick Add Lead (manual intake) ----
  function resetQa() {
    setQaName("");
    setQaUrl("");
    setQaSource("twitter");
    setQaSnippet("");
    setQaScore(5);
  }

  async function quickAddSubmit(e) {
    e.preventDefault();
    if (qaBusy) return;

    // Minimal validation
    const name = qaName.trim();
    const source = qaSource.trim().toLowerCase();
    const url = qaUrl.trim();
    const snippet = qaSnippet.trim();
    let score = Number(qaScore);
    if (!name) {
      setBanner({ type: "error", msg: "Name is required." });
      return;
    }
    if (!["twitter", "rss"].includes(source)) {
      setBanner({ type: "error", msg: "Source must be Twitter or RSS." });
      return;
    }
    if (Number.isNaN(score)) score = 0;
    score = Math.max(0, Math.min(10, Math.round(score)));

    setQaBusy(true);

    // Insert to DB
    const { data, error } = await supabase
      .from("leads_discovery")
      .insert([
        {
          name,
          profile_url: url || null,
          source,
          context_snippet: snippet || null,
          lead_score: score,
          status: "New",
        },
      ])
      .select("*")
      .single();

    if (error) {
      console.error(error);
      setBanner({ type: "error", msg: "Add failed." });
      setQaBusy(false);
      return;
    }

    // Prepend to UI instantly
    setRows((prev) => [data, ...prev]);
    setBanner({ type: "success", msg: "Lead added to Discovery." });
    bumpBadge(1);
    setQaBusy(false);
    resetQa();
    setQaOpen(false);
  }

  // ---- CSV Import ----
  function validateCsvHeader(header) {
    const required = ["name", "profile_url", "source", "context_snippet", "lead_score"];
    const lower = header.map((h) => h.toLowerCase().trim());
    for (const r of required) {
      if (!lower.includes(r)) return `Missing column: ${r}`;
    }
    return "";
  }

  function handleCsvPreview() {
    setCsvError("");
    const rows = parseCSV(csvText).filter((r) => r.length && r.join("").trim().length);
    if (rows.length === 0) {
      setCsvPreview([]);
      setCsvError("CSV is empty.");
      return;
    }
    const header = rows[0];
    const err = validateCsvHeader(header);
    if (err) {
      setCsvPreview([]);
      setCsvError(err + `\nExpected header: ${"name,profile_url,source,context_snippet,lead_score"}`);
      return;
    }
    const headerMap = header.map((h) => h.toLowerCase().trim());
    const data = rows.slice(1).map((r, idx) => {
      const obj = {};
      headerMap.forEach((key, i) => (obj[key] = r[i] ?? ""));
      return { __row: idx + 2, ...obj };
    });
    setCsvPreview(data);
  }

  async function handleCsvImport() {
    if (csvPreview.length === 0) {
      setCsvError("Nothing to import. Click Preview first.");
      return;
    }
    setCsvBusy(true);
    setCsvError("");

    // Build payload
    const payload = [];
    for (const rec of csvPreview) {
      const source = (rec.source || "").toLowerCase().trim();
      const scoreNum = Math.max(0, Math.min(10, Number(rec.lead_score) || 0));
      if (!rec.name) {
        setCsvBusy(false);
        setCsvError(`Row ${rec.__row}: name is required.`);
        return;
      }
      if (!["twitter", "rss"].includes(source)) {
        setCsvBusy(false);
        setCsvError(`Row ${rec.__row}: source must be "twitter" or "rss".`);
        return;
      }
      payload.push({
        name: rec.name,
        profile_url: rec.profile_url || null,
        source,
        context_snippet: rec.context_snippet || null,
        lead_score: scoreNum,
        status: "New",
      });
    }

    const { data, error } = await supabase
      .from("leads_discovery")
      .insert(payload)
      .select("*");

    if (error) {
      console.error(error);
      setCsvBusy(false);
      setCsvError("Import failed.");
      return;
    }

    // Update UI instantly
    setRows((prev) => [...data, ...prev]);
    bumpBadge(payload.length);
    setBanner({ type: "success", msg: `Imported ${payload.length} lead(s).` });
    setCsvBusy(false);
    setCsvPreview([]);
    setCsvText("");
    setCsvOpen(false);
  }

  function Banner({ notice }) {
    if (!notice) return null;
    const ok = notice.type === "success";
    const style = {
      padding: "10px 12px",
      marginBottom: 12,
      borderRadius: 8,
      fontSize: 14,
      background: ok ? "#e8f5e9" : "#ffebee",
      border: `1px solid ${ok ? "#c8e6c9" : "#ffcdd2"}`,
      color: ok ? "#1b5e20" : "#b71c1c",
    };
    return <div style={style}>{notice.msg}</div>;
  }

  // UI
  return (
    <div style={{ padding: 16 }}>
      <h1 style={{ marginBottom: 12 }}>Discovery</h1>

      <Banner notice={banner} />

      {/* Controls */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto auto auto auto auto auto", gap: 8, marginBottom: 12 }}>
        <input
          placeholder="Search name, source, snippet, URL…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ flex: 1, padding: 8, borderRadius: 8, border: "1px solid #ddd" }}
        />

        {/* Source Filter */}
        <select
          value={sourceFilter}
          onChange={(e) => {
            setSourceFilter(e.target.value);
            setPage(1);
          }}
          style={{ padding: 8, borderRadius: 8, border: "1px solid #ddd" }}
        >
          <option value="all">All sources</option>
          <option value="twitter">Twitter</option>
          <option value="rss">RSS</option>
        </select>

        {/* Score Sort */}
        <select
          value={scoreSort}
          onChange={(e) => setScoreSort(e.target.value)}
          style={{ padding: 8, borderRadius: 8, border: "1px solid #ddd" }}
        >
          <option value="desc">Score: High → Low</option>
          <option value="asc">Score: Low → High</option>
        </select>

        {/* Re-score */}
        <button
          onClick={rescoreSelected}
          disabled={selectedIds.size === 0 || bulkBusy}
          style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #ddd", background: selectedIds.size === 0 || bulkBusy ? "#eee" : "#fafafa" }}
        >
          Re-score Selected
        </button>
        <button
          onClick={rescoreAllFiltered}
          disabled={filtered.length === 0 || bulkBusy}
          style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #ddd", background: filtered.length === 0 || bulkBusy ? "#eee" : "#fafafa" }}
        >
          Re-score All (Filtered)
        </button>

        {/* Quick Add */}
        <button
          onClick={() => setQaOpen((v) => !v)}
          disabled={bulkBusy}
          style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #ddd", background: "#fafafa" }}
        >
          {qaOpen ? "Close Add" : "Add Lead"}
        </button>

        {/* CSV Import */}
        <button
          onClick={() => setCsvOpen((v) => !v)}
          disabled={bulkBusy}
          style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #ddd", background: "#fafafa" }}
        >
          {csvOpen ? "Close CSV" : "Import CSV"}
        </button>

        {/* Add URL (scrape-to-ingest) */}
        <button
          onClick={() => setAddUrlOpen((v) => !v)}
          disabled={bulkBusy}
          style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #ddd", background: "#fafafa" }}
        >
          {addUrlOpen ? "Close Add URL" : "Add URL"}
        </button>

        {/* Refresh */}
        <button
          onClick={fetchRows}
          disabled={bulkBusy}
          style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #ddd", background: "#fafafa" }}
        >
          Refresh
        </button>
      </div>

      {/* Quick Add panel */}
      {qaOpen && (
        <form onSubmit={quickAddSubmit} style={{ border: "1px solid #eee", padding: 12, borderRadius: 10, marginBottom: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 140px 1fr 120px 120px", gap: 8 }}>
            <input placeholder="Name *" value={qaName} onChange={(e) => setQaName(e.target.value)} style={{ padding: 8, borderRadius: 8, border: "1px solid #ddd" }} />
            <input placeholder="Profile URL" value={qaUrl} onChange={(e) => setQaUrl(e.target.value)} style={{ padding: 8, borderRadius: 8, border: "1px solid #ddd" }} />
            <select value={qaSource} onChange={(e) => setQaSource(e.target.value)} style={{ padding: 8, borderRadius: 8, border: "1px solid #ddd" }}>
              <option value="twitter">Twitter</option>
              <option value="rss">RSS</option>
            </select>
            <input placeholder="Context snippet" value={qaSnippet} onChange={(e) => setQaSnippet(e.target.value)} style={{ padding: 8, borderRadius: 8, border: "1px solid #ddd" }} />
            <input type="number" min={0} max={10} step={1} placeholder="Score (0–10)" value={qaScore} onChange={(e) => setQaScore(e.target.value)} style={{ padding: 8, borderRadius: 8, border: "1px solid #ddd" }} />
            <button type="submit" disabled={qaBusy} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #c8e6c9", background: qaBusy ? "#d7e8da" : "#e8f5e9", cursor: qaBusy ? "not-allowed" : "pointer" }}>
              Add
            </button>
          </div>
        </form>
      )}

      {/* Add URL panel */}
      {addUrlOpen && (
        <div style={{ border: "1px solid #eee", padding: 12, borderRadius: 10, marginBottom: 12 }}>
          <div style={{ marginBottom: 8, fontSize: 14 }}>
            Paste a URL to scrape its title/description straight into Discovery for triage.
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              style={{ flex: 1, padding: 8, border: "1px solid #ddd", borderRadius: 6 }}
              placeholder="https://…"
              value={addUrlValue}
              onChange={(e) => setAddUrlValue(e.target.value)}
            />
            <button
              onClick={saveAddUrl}
              disabled={addUrlBusy}
              style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #c8e6c9", background: addUrlBusy ? "#d7e8da" : "#e8f5e9", cursor: addUrlBusy ? "not-allowed" : "pointer" }}
            >
              {addUrlBusy ? "Saving…" : "Save URL"}
            </button>
          </div>
        </div>
      )}

      {/* CSV Import panel */}
      {csvOpen && (
        <div style={{ border: "1px solid #eee", padding: 12, borderRadius: 10, marginBottom: 12 }}>
          <div style={{ marginBottom: 8, fontSize: 14 }}>
            Paste CSV with header: <code>name,profile_url,source,context_snippet,lead_score</code>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 140px 140px", gap: 8 }}>
            <textarea
              placeholder={csvTemplate}
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              rows={8}
              style={{ width: "100%", padding: 8, borderRadius: 8, border: "1px solid #ddd", fontFamily: "monospace" }}
            />
            <button
              onClick={handleCsvPreview}
              disabled={csvBusy}
              style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #ddd", background: "#fafafa", height: 42 }}
            >
              Preview
            </button>
            <button
              onClick={handleCsvImport}
              disabled={csvBusy || csvPreview.length === 0}
              style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #c8e6c9", background: csvBusy || csvPreview.length === 0 ? "#d7e8da" : "#e8f5e9", height: 42, cursor: csvBusy || csvPreview.length === 0 ? "not-allowed" : "pointer" }}
            >
              Import
            </button>
          </div>
          {csvError ? <div style={{ marginTop: 8, color: "#b71c1c" }}>{csvError}</div> : null}
          {csvPreview.length > 0 && (
            <div style={{ marginTop: 12, fontSize: 14 }}>
              Previewing {csvPreview.length} row(s). First 5:
              <ul style={{ marginTop: 6 }}>
                {csvPreview.slice(0, 5).map((r, i) => (
                  <li key={i}>
                    {r.name} | {r.source} | {r.lead_score} | {r.profile_url} | {r.context_snippet?.slice(0, 60) ?? ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div>Loading…</div>
      ) : filtered.length === 0 ? (
        <div>No discovery leads.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ padding: 8, borderBottom: "1px solid #eee" }}>
                  <input type="checkbox" checked={isAllOnPageSelected} onChange={toggleSelectAllOnPage} />
                </th>
                {["Name", "Source", "Score", "Date Added", "Snippet", "Actions"].map((h) => (
                  <th key={h} style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #eee" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r) => (
                <tr key={r.id}>
                  <td style={{ padding: 8, borderBottom: "1px solid #f4f4f4" }}>
                    <input type="checkbox" checked={selectedIds.has(r.id)} onChange={() => toggleSelect(r.id)} disabled={busyIds.has(r.id) || bulkBusy} />
                  </td>
                  <td style={{ padding: 8, borderBottom: "1px solid #f4f4f4" }}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <strong>{r.name || "—"}</strong>
                      {r.profile_url ? <a href={r.profile_url} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>{r.profile_url}</a> : null}
                    </div>
                  </td>
                  <td style={{ padding: 8, borderBottom: "1px solid #f4f4f4" }}>{r.source || "—"}</td>
                  <td style={{ padding: 8, borderBottom: "1px solid " + (r.lead_score >= 7 ? "#d1e7dd" : "#f4f4f4") }}>{r.lead_score ?? 0}</td>
                  <td style={{ padding: 8, borderBottom: "1px solid #f4f4f4" }}>{r.date_added ? new Date(r.date_added).toLocaleString() : "—"}</td>
                  <td style={{ padding: 8, borderBottom: "1px solid #f4f4f4", maxWidth: 480 }}>
                    <div style={{ whiteSpace: "pre-wrap" }}>{r.context_snippet || "—"}</div>
                  </td>
                  <td style={{ padding: 8, borderBottom: "1px solid #f4f4f4" }}>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        onClick={() => approve(r.id)}
                        disabled={busyIds.has(r.id) || bulkBusy}
                        style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #c8e6c9", background: busyIds.has(r.id) || bulkBusy ? "#d7e8da" : "#e8f5e9", cursor: busyIds.has(r.id) || bulkBusy ? "not-allowed" : "pointer" }}
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => reject(r.id)}
                        disabled={busyIds.has(r.id) || bulkBusy}
                        style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #ffcdd2", background: busyIds.has(r.id) || bulkBusy ? "#f3d6da" : "#ffebee", cursor: busyIds.has(r.id) || bulkBusy ? "not-allowed" : "pointer" }}
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Bulk action bar */}
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12 }}>
            <button
              onClick={approveSelected}
              disabled={selectedIds.size === 0 || bulkBusy}
              style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #c8e6c9", background: selectedIds.size === 0 || bulkBusy ? "#d7e8da" : "#e8f5e9", cursor: selectedIds.size === 0 || bulkBusy ? "not-allowed" : "pointer" }}
            >
              Approve Selected
            </button>
            <button
              onClick={rejectSelected}
              disabled={selectedIds.size === 0 || bulkBusy}
              style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #ffcdd2", background: selectedIds.size === 0 || bulkBusy ? "#f3d6da" : "#ffebee", cursor: selectedIds.size === 0 || bulkBusy ? "not-allowed" : "pointer" }}
            >
              Reject Selected
            </button>

            {/* Pagination */}
            <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={pageSafe <= 1} style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #ddd", background: "#fafafa" }}>
                Prev
              </button>
              <div>Page {pageSafe} of {totalPages}</div>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={pageSafe >= totalPages} style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #ddd", background: "#fafafa" }}>
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
